import { ChatConversationLine } from "@/components/chat/chat-conversation-line";
import { ProspectingChatAnnouncement } from "@/components/chat/prospecting-chat-announcement";
import PageSkeleton from "@/components/page-skeleton";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useChatColumns } from "@/hooks/useChatColumns";
import { useIsWideDesktop } from "@/hooks/useIsWideDesktop";
import { useProspectingAnnouncement } from "@/hooks/useProspectingAnnouncement";
import { useTranslation } from "@/hooks/useTranslation";
import {
  conversationListLayout,
  INITIAL_CONVERSATION_LIST_STATE,
  nextConversationListState,
} from "@/lib/conversation-list-layout";
import { groupConversationsByTime } from "@/lib/conversation-time-groups";
import { cn } from "@/lib/utils";
import { useShownPeopleSearchId } from "@/stores/chat-artifact-panel-store";
import { ConversationDTO, ConversationsListResponse } from "@shaarp/common";
import { Search, SquarePenIcon, X } from "lucide-react";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { ChatConversationPage } from "./chat/conversation";

export default function ChatPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const announcement = useProspectingAnnouncement();
  const { conversationId } = useParams();
  const toastError = useToastError();

  const [conversationList, dispatchConversationList] = useReducer(
    nextConversationListState,
    INITIAL_CONVERSATION_LIST_STATE,
  );

  // Two columns at most on a screen narrower than three need: a panel opening
  // on the right folds the list away and gives it back when it closes; asked
  // for meanwhile, the list comes over the chat.
  const { isCrowded } = useChatColumns();
  useEffect(() => {
    dispatchConversationList({ type: isCrowded ? "panelOpened" : "panelClosed" });
  }, [isCrowded]);
  const conversationListPlacement = conversationListLayout({
    isOpen: conversationList.isOpen,
    isCrowded,
  });

  // A table of people needs the width even on a wide screen: opening one
  // folds the conversation list away. Keyed on the search, not on the panel,
  // so the table filling up does not fold it again once the user has reopened
  // it — nor re-render the page and its conversation list on each row it
  // streams. Narrower screens already fold it through the rule above; a
  // screen growing wide gives it back there, so this folds it again (declared
  // after that rule, it runs after it).
  const shownPeopleSearchId = useShownPeopleSearchId();
  const isWide = useIsWideDesktop();
  useEffect(() => {
    if (shownPeopleSearchId && isWide) {
      dispatchConversationList({ type: "close" });
    }
  }, [shownPeopleSearchId, isWide]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [conversations, setConversations] = useState<ConversationDTO[]>([]);
  // Full set of pinned conversations, resolved by the API on the first page.
  // Kept separate from the activity feed so a stale-but-pinned conversation
  // always shows in the sidebar's "pinned" section regardless of paging.
  const [pinnedConversations, setPinnedConversations] = useState<
    ConversationDTO[]
  >([]);
  const [conversationSelected, setConversationSelected] =
    useState<ConversationDTO>();

  const [filterMessage, setFilterMessage] = useState<string>("");

  const nextCursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef<boolean>(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  // Debounced server-side search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      fetchConversations(filterMessage);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [filterMessage]);

  useEffect(() => {
    if (conversations.length === 0) {
      return;
    }

    if (!conversationId) {
      setConversationSelected(undefined);
    } else {
      const convSelected = conversations.find((c) => c.id === conversationId);
      if (convSelected) {
        setConversationSelected(convSelected);
      } else {
        navigate("/chat", { replace: true });
      }
    }
  }, [conversations, conversationId]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingMore) {
          fetchMoreConversations();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isLoadingMore]);

  const fetchConversations = useCallback(async (search?: string) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (search) {
        params.set("search", search);
      }
      const { data } = await api.get<ConversationsListResponse>(
        `/conversations?${params.toString()}`
      );
      const sorted = [...data.data].sort(
        (a, b) =>
          new Date(b.lastMessageAt).getTime() -
          new Date(a.lastMessageAt).getTime()
      );
      setConversations(sorted);
      setPinnedConversations(data.pinned);
      nextCursorRef.current = data.nextCursor;
      hasMoreRef.current = data.hasMore;
    } catch (error) {
      toastError();
      setConversations([]);
      setPinnedConversations([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchMoreConversations = useCallback(async () => {
    if (!nextCursorRef.current || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const params = new URLSearchParams({
        limit: "30",
        cursor: nextCursorRef.current,
      });
      if (filterMessage) {
        params.set("search", filterMessage);
      }
      const { data } = await api.get<ConversationsListResponse>(
        `/conversations?${params.toString()}`
      );
      const newConversations = data.data;
      setConversations((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const unique = newConversations.filter((c) => !existingIds.has(c.id));
        return [...prev, ...unique];
      });
      nextCursorRef.current = data.nextCursor;
      hasMoreRef.current = data.hasMore;
    } catch (error) {
      toastError();
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, filterMessage]);

  const onConversationCreated = (conversation: ConversationDTO) => {
    setConversations([conversation, ...conversations]);
  };

  const onConversationPinToggled = (updated: ConversationDTO) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === updated.id ? { ...c, pinnedAt: updated.pinnedAt } : c
      )
    );
    setPinnedConversations((prev) => {
      const withoutUpdated = prev.filter((c) => c.id !== updated.id);
      return updated.pinnedAt ? [updated, ...withoutUpdated] : withoutUpdated;
    });
  };

  const onConversationRenamed = (updated: ConversationDTO) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === updated.id ? { ...c, title: updated.title } : c
      )
    );
    setPinnedConversations((prev) =>
      prev.map((c) =>
        c.id === updated.id ? { ...c, title: updated.title } : c
      )
    );
  };

  const renderConversationGroups = () => {
    const pinned = [...pinnedConversations].sort(
      (a, b) =>
        new Date(b.pinnedAt as Date).getTime() -
        new Date(a.pinnedAt as Date).getTime()
    );
    // While searching, the dedicated "pinned" section is empty (the API only
    // resolves it on the unfiltered first page), so keep pinned conversations
    // in the time-grouped feed instead — otherwise a pinned conversation that
    // matches the search would be filtered out here and vanish from results.
    const isSearching = filterMessage.trim().length > 0;
    const unpinned = isSearching
      ? conversations
      : conversations.filter((c) => !c.pinnedAt);
    const grouped = groupConversationsByTime(unpinned);

    const renderGroup = (titleKey: string, conversations: ConversationDTO[]) =>
      conversations.length > 0 && (
        <div key={titleKey} className="space-y-2">
          <h3 className="ml-2 text-xs font-semibold uppercase text-majorelle">
            {t(`chat.page.timeGroups.${titleKey}`)}
          </h3>
          {conversations.map((conversation) => (
            <ChatConversationLine
              key={conversation.id}
              conversation={conversation}
              isSelected={conversation.id === conversationSelected?.id}
              searchTerm={filterMessage}
              onRemoved={() => fetchConversations()}
              onPinToggled={onConversationPinToggled}
              onRenamed={onConversationRenamed}
            />
          ))}
        </div>
      );

    return (
      <div className="space-y-6">
        {renderGroup("pinned", pinned)}
        {renderGroup("today", grouped.today)}
        {renderGroup("yesterday", grouped.yesterday)}
        {renderGroup("thisWeek", grouped.thisWeek)}
        {renderGroup("previousWeek", grouped.previousWeek)}
        {renderGroup("thisMonth", grouped.thisMonth)}
        {renderGroup("previousMonth", grouped.previousMonth)}
        {renderGroup("older", grouped.older)}
      </div>
    );
  };

  return (
    <>
      {announcement.isActive && (
        <div className="absolute top-(--app-header-height) left-0 w-full">
          <ProspectingChatAnnouncement
            isBannerShown={announcement.isBannerShown}
            onBannerDismiss={announcement.dismissBanner}
          />
        </div>
      )}
      {/* The header, plus the announcement banner while it shows. */}
      <div
        className={cn(
          "absolute left-0 flex-1 w-full overflow-auto",
          announcement.isBannerShown
            ? "top-[calc(var(--app-header-height)+var(--app-banner-height))] h-[calc(100%-var(--app-header-height)-var(--app-banner-height))]"
            : "top-(--app-header-height) h-[calc(100%-var(--app-header-height))]",
        )}
      >
        {conversationListPlacement === "overlay" && (
          // Over the chat, the list is put away by a click beside it.
          <div
            aria-hidden
            className="absolute inset-0 z-30 bg-black/30 animate-in fade-in duration-300"
            onClick={() => dispatchConversationList({ type: "close" })}
          />
        )}
        <div className="flex flex-row w-full h-full">
          <div
            className={cn(
              "flex relative shrink-0 w-(--chat-sidebar-width) ease-in-out duration-300",
              conversationListPlacement !== "inline" &&
                "-ml-(--chat-sidebar-width)",
            )}
          >
            <div
              className={cn(
                "overflow-auto absolute top-0 left-0 flex-1 p-2 pt-6 compact:pt-3 space-y-5 w-full h-full bg-background",
                // Folded off to the left, the list slides back over the chat.
                "transition-transform ease-in-out duration-300",
                conversationListPlacement === "overlay" &&
                  "z-40 translate-x-full border-r shadow-2xl",
              )}
            >
              <div className="space-y-2">
                <div className="flex-1 p-1 pt-0 space-y-5 compact:space-y-3">
                  <div className="flex justify-between">
                    <h2 className="text-2xl compact:text-xl font-bold tracking-tight">
                      {t("chat.page.title")}
                    </h2>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                    <Input
                      placeholder={t("chat.page.searchPlaceholder")}
                      className="pr-8 pl-9 w-full bg-background"
                      value={filterMessage}
                      onChange={(e) => setFilterMessage(e.target.value)}
                    />
                    {filterMessage && (
                      <button
                        type="button"
                        onClick={() => setFilterMessage("")}
                        className="absolute right-2 top-1/2 transition-colors -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <NavLink
                    className={cn(buttonVariants(), "gap-2 w-full")}
                    to={`/chat`}
                  >
                    <SquarePenIcon className="w-4 h-4" />
                    {t("chat.page.newConversation")}
                  </NavLink>
                </div>

                <div className="flex flex-col gap-2 p-0 pt-0">
                  {isLoading && <PageSkeleton />}

                  {!isLoading && conversations.length === 0 && (
                    <div className="flex flex-col justify-center items-center py-8 w-full">
                      <p
                        className="text-center text-muted-foreground"
                        dangerouslySetInnerHTML={{
                          __html: t("chat.page.noConversations"),
                        }}
                      />
                    </div>
                  )}
                  {!isLoading &&
                    conversations.length > 0 &&
                    renderConversationGroups()}

                  {/* Sentinel for infinite scroll */}
                  <div ref={sentinelRef} className="h-1" />
                  {isLoadingMore && <PageSkeleton />}
                </div>
              </div>
            </div>
          </div>
          <div className="relative w-full border-l">
            <ChatConversationPage
              key={conversationSelected?.id}
              conversation={conversationSelected}
              onCreated={onConversationCreated}
              onSidebarToggle={() =>
                dispatchConversationList({ type: "toggle" })
              }
            />
          </div>
        </div>
      </div>
    </>
  );
}
